import { useCallback, useEffect, useRef } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, CloudOff, FlaskConical, Server } from "lucide-react";
import { EASE_OUT } from "../lib/motion";

/**
 * Tilt limits, in degrees. Kept small on purpose: this card carries the primary
 * action, and a stronger tilt slid the buttons out from under the pointer as it
 * approached them. Well damped for the same reason, so the card settles instead
 * of wobbling.
 */
const TILT_Y = 4;
const TILT_X = 3;
const TILT_SPRING = { stiffness: 150, damping: 24, mass: 0.6 };

const PARTICLES = [
  { left: "12%", top: "22%", delay: 0 },
  { left: "84%", top: "18%", delay: 0.9 },
  { left: "22%", top: "78%", delay: 1.8 },
  { left: "72%", top: "82%", delay: 2.6 },
  { left: "50%", top: "12%", delay: 3.4 },
];

/**
 * Shown when someone reaches for an upload while the backend is unreachable.
 *
 * Uploading needs the service, so rather than let them pick a file and fail, the
 * dropzone hands them here and offers the one thing that does work offline: the
 * bundled sample, whose result is captured from a real run.
 *
 * The card tilts toward the pointer on a spring, with its contents on separate
 * Z planes so the tilt reads as depth rather than as a skew. All of it collapses
 * to a plain centred card under `prefers-reduced-motion`.
 */
function OfflineModal({ open, onClose, onRunSample }) {
  const prefersReducedMotion = useReducedMotion();
  const cardRef = useRef(null);
  const closeRef = useRef(null);
  const restoreFocusTo = useRef(null);

  // Pointer-driven tilt, clamped so text stays readable and targets stay put.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-TILT_Y, TILT_Y]), TILT_SPRING);
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [TILT_X, -TILT_X]), TILT_SPRING);
  const glowX = useTransform(px, [-0.5, 0.5], ["25%", "75%"]);
  const glowY = useTransform(py, [-0.5, 0.5], ["25%", "75%"]);

  const onPointerMove = useCallback(
    (event) => {
      const card = cardRef.current;
      if (!card || prefersReducedMotion) return;
      const rect = card.getBoundingClientRect();
      px.set((event.clientX - rect.left) / rect.width - 0.5);
      py.set((event.clientY - rect.top) / rect.height - 0.5);
    },
    [prefersReducedMotion, px, py]
  );

  const resetTilt = useCallback(() => {
    px.set(0);
    py.set(0);
  }, [px, py]);

  // Escape closes, focus moves in on open and back to the trigger on close.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(focusTimer);
      if (restoreFocusTo.current instanceof HTMLElement) restoreFocusTo.current.focus();
    };
  }, [open, onClose]);

  const enter = prefersReducedMotion
    ? { duration: 0 }
    : { type: "spring", stiffness: 260, damping: 22, mass: 0.8 };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="offline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.22, ease: EASE_OUT }}
          onPointerMove={onPointerMove}
          onPointerLeave={resetTilt}
          onClick={onClose}
        >
          <motion.div
            ref={cardRef}
            className="offline__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="offline-title"
            aria-describedby="offline-body"
            style={prefersReducedMotion ? undefined : { rotateX, rotateY }}
            initial={{ opacity: 0, scale: 0.9, y: 26 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={enter}
            // Clicks inside must not reach the backdrop's dismiss handler.
            onClick={(event) => event.stopPropagation()}
          >
            {!prefersReducedMotion && (
              <motion.span
                className="offline__glow"
                aria-hidden="true"
                style={{ left: glowX, top: glowY }}
              />
            )}
            <span className="offline__grid" aria-hidden="true" />

            {!prefersReducedMotion &&
              PARTICLES.map((particle) => (
                <span
                  key={particle.left + particle.top}
                  className="offline__particle"
                  aria-hidden="true"
                  style={{
                    left: particle.left,
                    top: particle.top,
                    animationDelay: `${particle.delay}s`,
                  }}
                />
              ))}

            <div className="offline__inner">
              <motion.span
                className="offline__icon"
                aria-hidden="true"
                initial={prefersReducedMotion ? false : { scale: 0.6, rotate: -12 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 300, damping: 14, delay: 0.08 }
                }
              >
                <CloudOff size={26} />
                <span className="offline__ping" />
              </motion.span>

              <span className="badge badge--warn">
                <Server size={11} aria-hidden="true" />
                Service offline
              </span>

              <h2 className="offline__title" id="offline-title">
                Uploads need the backend
              </h2>

              <p className="offline__body" id="offline-body">
                The analysis service is not responding, so a file you upload cannot be processed
                right now. The bundled sample still works: its result was captured from a real run,
                so you can see everything the tool produces.
              </p>

              <div className="offline__actions">
                <button type="button" className="btn btn--primary btn--lg" onClick={onRunSample}>
                  <span className="btn__sheen" aria-hidden="true" />
                  <FlaskConical size={16} aria-hidden="true" />
                  Load the sample
                  <ArrowRight size={15} aria-hidden="true" />
                </button>
                <button type="button" className="btn btn--ghost" onClick={onClose} ref={closeRef}>
                  Not now
                </button>
              </div>

              <p className="offline__hint">
                Running it locally? Start the service with{" "}
                <code>uvicorn api:app --reload</code>
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default OfflineModal;
